import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Star, Quote, CheckCircle2 } from 'lucide-react'
import { scrapedData } from '../data'
import { renderFormattedText } from '../utils/textFormatter'
import SEO from '../components/SEO'

export default function Testimonials() {
  const { godeErfaringer } = scrapedData
  const [activeTab, setActiveTab] = useState('karin')

  const otherTestimonials = [
    {
      id: "mette",
      client_name: "Mette S.",
      treatment_received: "Muskelterapi & Rørbæk Teknikken",
      rating: 5,
      story: "Efter flere år med kroniske lændesmerter og adskillige forgæves forsøg hos andre behandlere, prøvede jeg Rørbæk Teknikken. Allerede efter første behandling mærkede jeg en markant lindring, og efter fire behandlinger er jeg stort set smertefri. Det har givet mig min livskvalitet tilbage!"
    },
    {
      id: "thomas",
      client_name: "Thomas J.",
      treatment_received: "Kranio-Sakral Terapi",
      rating: 5,
      story: "Jeg kom til klinikken med svær stress, hjertebanken og søvnløshed. Kranio-Sakral-behandlingen har været en øjenåbner for mig. Den dybe ro, man mærker under og efter behandlingen, er helt ubeskrivelig. Min krop er faldet helt til ro, og jeg sover fantastisk igen."
    },
    {
      id: "hanne",
      client_name: "Hanne Poulsen",
      treatment_received: "Fysiurgisk massage & TDP-termomassage",
      rating: 5,
      story: "Kombinationen af den dybe varme fra TDP-lampen og den faste, faglige massage gør underværker for mine gigtplagede skuldre. Inge-Merete er utrolig dygtig, lyttende og professionel. Man føler sig altid i trygge hænder."
    }
  ]

  return (
    <div className="space-y-20 pb-16">
      <SEO
        title="Klienters erfaringer & Anbefalinger | Rørbækklinikken"
        description="Læs virkelige klienthistorier om hjælp mod bækkenløsning, spædbørnsuro, kroniske rygsmerter og stress hos Rørbækklinikken i Viborg."
      />
      
      {/* Page Header */}
      <section className="bg-[#F5F0E6]/40 py-16 border-b border-[#E6DFD3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#1E352F]">
            Gode erfaringer fra vores klienter
          </h1>
          <p className="text-base sm:text-lg text-[#1E352F]/80 leading-relaxed">
            Læs historier og udtalelser fra klienter, der har gennemgået et behandlingsforløb hos Rørbækklinikken I/S. Deres oplevelser er vores største stolthed.
          </p>
        </div>
      </section>

      {/* Main Story & Selector Tabs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="flex flex-wrap justify-center gap-2 border-b border-[#E6DFD3] pb-6">
          <button
            onClick={() => setActiveTab('karin')}
            className={`px-4 py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer ${
              activeTab === 'karin'
                ? 'bg-[#1E352F] text-white shadow-sm'
                : 'bg-white text-[#1E352F]/80 border border-[#E6DFD3] hover:bg-[#F5F0E6]/50'
            }`}
          >
            Karins historie (Bækkenløsning)
          </button>
          <button
            onClick={() => setActiveTab('others')}
            className={`px-4 py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer ${
              activeTab === 'others'
                ? 'bg-[#1E352F] text-white shadow-sm'
                : 'bg-white text-[#1E352F]/80 border border-[#E6DFD3] hover:bg-[#F5F0E6]/50'
            }`}
          >
            Flere udtalelser
          </button>
        </div>

        {/* Karin's Detailed Story */}
        {activeTab === 'karin' ? (
          <div className="bg-white rounded-2xl border border-[#E6DFD3] p-8 sm:p-12 shadow-sm max-w-4xl mx-auto relative overflow-hidden">
            <div className="absolute top-6 right-6 text-[#F5F0E6] pointer-events-none">
              <Quote className="w-24 h-24 fill-current" />
            </div>
            <div className="space-y-6 relative z-10">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-0 border-b border-[#F5F0E6] pb-4">
                <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1E352F]">
                  {godeErfaringer.title}
                </h2>
                <span className="text-xs sm:text-sm font-semibold text-[#eebb53] bg-[#eebb53]/10 px-3 py-1 rounded-full self-start lg:self-auto">
                  Af: Karin, 31 år
                </span>
              </div>
              <div className="rounded-xl overflow-hidden max-h-80 border border-[#E6DFD3]">
                <img
                  src="https://assets.auxodev.dk/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/e32e361a-9003-4723-a9c4-6c8c7a034e43.jpg"
                  alt="Kranio-Sakral Terapi mod bækkenløsning"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="text-sm sm:text-base text-[#1E352F]/80 leading-relaxed space-y-4 italic">
                {renderFormattedText(godeErfaringer.content, "font-serif text-2xl font-bold text-[#1E352F] pt-6 pb-2 not-italic")}
              </div>
            </div>
          </div>
        ) : (
          /* Other Testimonials Grid */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {otherTestimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E6DFD3] shadow-sm flex flex-col justify-between space-y-6 relative overflow-hidden"
              >
                <div className="absolute top-6 right-6 text-[#F5F0E6] pointer-events-none">
                  <Quote className="w-16 h-16 fill-current" />
                </div>
                <div className="space-y-4 relative z-10">
                  <div className="flex items-center gap-1 text-amber-500">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                  <p className="text-sm sm:text-base text-[#1E352F]/80 italic leading-relaxed">
                    {testimonial.story}
                  </p>
                </div>
                <div className="border-t border-[#F5F0E6] pt-4 flex justify-between items-center text-xs sm:text-sm relative z-10">
                  <span className="font-bold text-[#1E352F] flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    {testimonial.client_name}
                  </span>
                  <span className="text-[#1E352F]/50 font-medium">
                    {testimonial.treatment_received}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Trust & Ethics Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#F5F0E6] rounded-2xl p-6 sm:p-8 border border-[#E6DFD3] text-center space-y-4">
          <h3 className="font-serif text-xl font-bold text-[#1E352F]">
            Vil du dele din oplevelse med os?
          </h3>
          <p className="text-sm text-[#1E352F]/80 leading-relaxed max-w-2xl mx-auto">
            Vi sætter utrolig stor pris på feedback fra vores klienter. Hvis du har været glad for dit forløb hos os, er du altid velkommen til at sende os en kort besked eller skrive en anmeldelse. Det hjælper andre med at finde den rette hjælp.
          </p>
          <div className="pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 bg-[#1E352F] hover:bg-[#2D4A43] text-white px-6 py-2.5 rounded-full text-sm font-semibold transition-colors"
            >
              Skriv til os
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#1E352F] rounded-2xl text-[#F5F0E6] p-8 sm:p-12 text-center space-y-6 border border-[#2D4A43]">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white">
            Klar til at opleve en mærkbar forskel?
          </h2>
          <p className="text-sm sm:text-base text-[#F5F0E6]/80 max-w-2xl mx-auto leading-relaxed">
            Tilmeld dig de mange tilfredse klienter i Viborg, der har fået hjælp til at slippe af med smerter og spændinger. Book din tid online i dag.
          </p>
          <div className="pt-2">
            <a
              href="https://roerbaekklinikken.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#eebb53] hover:bg-[#dca63d] text-white px-8 py-3.5 rounded-full text-base font-semibold transition-colors shadow-sm"
            >
              <Calendar className="w-5 h-5" />
              Book tid online
            </a>
          </div>
        </div>
      </section>

    </div>
  )
}
